// VaultSync Authentication panel (#view-vssauth). Drives the flows in
// vss-auth.js: enroll this device, add a device by QR, approve, revoke.
// Self-wires on the "Sign-in" nav item; everything it calls is the verified
// VSS API. QR renders via window.qrcode (qrcode-generator) if present, else the
// add-device URL is shown to copy/open on the phone.

import * as vss from './vss-auth.js';
import * as events from './events.js';

const $ = (id) => document.getElementById(id);
const authHdr = () => { const t = localStorage.getItem('gdbs_token') || ''; return { 'Content-Type': 'application/json', ...(t ? { Authorization: 'Bearer ' + t } : {}) }; };
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function userIdFromToken() {
  const t = localStorage.getItem('gdbs_token');
  if (!t) return null;
  try {
    const c = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return c.userId || c.sub || c.nameid || c['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || null;
  } catch { return null; }
}

async function renderQr(container, url) {
  container.innerHTML = '<p class="vss-muted" style="font-size:12px;">Generating QR...</p>';
  try {
    const tok = localStorage.getItem('gdbs_token') || '';
    const r = await fetch('/api/auth/vss/qr', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
      body: JSON.stringify({ data: url }),
    });
    if (r.ok) {
      const j = await r.json();
      if (j.svg) { container.innerHTML = `<div style="max-width:220px;">${j.svg}</div>`; return; }
    }
  } catch { /* fall through */ }
  container.innerHTML = `<p class="vss-muted" style="font-size:12px;">Open this link on your phone:</p>`;
}

async function render() {
  const body = $('vssauth-body');
  if (!body) return;
  const userId = userIdFromToken();
  if (!userId) { body.innerHTML = '<p style="color:#b91c1c;">Sign in first to manage VaultSync Authentication.</p>'; return; }
  if (!(window.crypto && crypto.subtle && crypto.subtle.generateKey)) {
    body.innerHTML = '<p style="color:var(--accent-red,#b91c1c);">This browser lacks the WebCrypto (P-256) support VaultSync needs.</p>';
    return;
  }

  body.innerHTML = `
    <style>
      .vss-btn{font:inherit;font-size:13px;padding:7px 14px;border-radius:8px;border:1px solid var(--accent,#7c3aed);background:var(--accent,#7c3aed);color:#fff;cursor:pointer;}
      .vss-btn.secondary{background:var(--bg-panel,#fff);color:var(--accent,#7c3aed);}
      .vss-btn-danger{background:transparent;color:var(--accent-red,#b91c1c);border-color:var(--border,#fecaca);padding:5px 10px;}
      .vss-card{border:1px solid var(--border,#e2e8f0);border-radius:12px;padding:16px;margin-top:14px;background:var(--bg-panel,#fff);color:var(--text,#334155);}
      .vss-card .vss-muted{color:var(--text-dim,#64748b);}
      #vss-msg{color:var(--text-secondary,#475569);}
      #vss-url{background:var(--bg-input,#fff);color:var(--text,#334155);border:1px solid var(--border,#e2e8f0);}
    </style>
    <div class="vss-card">
      <strong>Profile</strong>
      <div style="margin-top:10px;display:grid;gap:8px;max-width:380px;">
        <label class="vss-muted" style="font-size:12px;">Display name
          <input id="vss-name" style="display:block;width:100%;margin-top:2px;padding:8px;border-radius:8px;border:1px solid var(--border,#e2e8f0);background:var(--bg-input,#fff);color:var(--text,#334155);" /></label>
        <label class="vss-muted" style="font-size:12px;">Email
          <input id="vss-email" type="email" style="display:block;width:100%;margin-top:2px;padding:8px;border-radius:8px;border:1px solid var(--border,#e2e8f0);background:var(--bg-input,#fff);color:var(--text,#334155);" /></label>
        <div><button id="vss-save-profile" class="vss-btn">Save profile</button></div>
        <p id="vss-profile-msg" class="vss-muted" style="font-size:12px;"></p>
      </div>
    </div>
    <div class="vss-card">
      <strong>This account's devices</strong>
      <div id="vss-devices"><p class="vss-muted" style="font-size:14px;">Loading...</p></div>
      <div style="margin-top:12px;display:flex;gap:10px;flex-wrap:wrap;">
        <button id="vss-enroll" class="vss-btn">Enroll this device</button>
        <button id="vss-add" class="vss-btn secondary">Add a device (QR)</button>
        <button id="vss-recover-code" class="vss-btn secondary">Recover with backup code</button>
        <button id="vss-recover-email" class="vss-btn secondary">Lost all devices? Email a link</button>
      </div>
      <p id="vss-msg" style="font-size:13px;color:#475569;margin-top:10px;"></p>
    </div>
    <div id="vss-add-area" class="vss-card" style="display:none;">
      <strong>Scan to add a device</strong>
      <div id="vss-qr" style="margin:12px 0;"></div>
      <input id="vss-url" readonly style="width:100%;font-size:12px;padding:8px;border-radius:8px;" />
      <p class="vss-muted" style="font-size:12px;margin-top:6px;">Open this on the new device, then return here and approve it (it appears automatically).</p>
    </div>`;

  const msg = (t) => { const m = $('vss-msg'); if (m) m.textContent = t; };

  $('vss-enroll').onclick = async () => {
    try {
      msg('Enrolling this device...');
      const r = await vss.enrollFirstDevice('this device');
      localStorage.setItem('vss_device_id', r.deviceId);
      alert('VaultSync backup code - save it now, shown only once:\n\n' + r.backupCode + '\n\nIt recovers your account if you lose every device.');
      msg('Enrolled. Backup code shown once - keep it safe.');
      render();
    } catch (e) { msg('Enroll failed: ' + (e.message || e)); }
  };

  $('vss-recover-code').onclick = async () => {
    const code = prompt('Enter your backup code:');
    if (!code) return;
    try { msg('Recovering with backup code...'); await vss.recoverWithBackupCode(code.trim(), 'recovered device'); localStorage.setItem('vss_device_id', ''); msg('Recovered and enrolled this device.'); render(); }
    catch (e) { msg('Backup-code recovery failed: ' + (e.message || e)); }
  };

  $('vss-recover-email').onclick = async () => {
    const email = prompt('Email to send a recovery link to:');
    if (!email) return;
    try { await fetch('/api/auth/vss/recover/email/start', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) }); msg('If that email matches an account, a recovery link is on its way.'); }
    catch (e) { msg('Could not send recovery link: ' + (e.message || e)); }
  };

  $('vss-add').onclick = async () => {
    try {
      msg('Creating add-device link...');
      const a = await vss.startAddDevice(userId);
      $('vss-add-area').style.display = '';
      $('vss-url').value = a.url;
      renderQr($('vss-qr'), a.url);
      msg('Link ready. Scan it on the new device - it will appear below for approval automatically.');
    } catch (e) { msg('Could not start add-device: ' + (e.message || e)); }
  };

  // Profile: load current values, save name/email.
  fetch('/api/auth/me', { headers: authHdr() }).then((r) => r.json()).then((d) => {
    const u = (d.data && d.data.user) || d.user;
    if (!u) return;
    if ($('vss-name')) $('vss-name').value = u.displayName || '';
    if ($('vss-email')) $('vss-email').value = u.email || '';
  }).catch(() => {});

  $('vss-save-profile').onclick = async () => {
    const pm = $('vss-profile-msg');
    try {
      // Sealed to the VaultSync session channel when present (PII over more than
      // TLS); falls back to a plain post for non-VSS sessions.
      const d = await vss.updateProfileSealed({ displayName: $('vss-name').value.trim(), email: $('vss-email').value.trim() });
      if (d && d.success === false) { pm.textContent = d.message || d.error || 'Update failed.'; return; }
      pm.textContent = vss.hasChannel() ? 'Saved (encrypted channel).' : 'Saved.';
      const disp = document.getElementById('user-display');
      if (disp) {
        disp.innerHTML = localStorage.getItem('vss_device_id') ? '<i class="fas fa-shield-halved" style="margin-right:6px;color:var(--accent,#7c3aed);"></i>' : '';
        disp.appendChild(document.createTextNode($('vss-name').value.trim() || $('vss-email').value.trim()));
      }
    } catch (e) { pm.textContent = 'Update failed: ' + (e.message || e); }
  };

  await renderDevices(userId, msg);

  // Refresh the device list (so a newly-added pending device + its Approve button
  // appear) only while the panel is visible.
  const refreshIfVisible = () => {
    const el = document.getElementById('vss-devices');
    if (el && el.offsetParent !== null) { renderDevices(userId, msg).catch(() => {}); return true; }
    return false;
  };

  // Event-driven: re-render when the server pushes a 'devices' event (a device was
  // added/approved/revoked for this account) instead of the old tight 3s poll.
  // The subscription self-cancels once the panel is hidden.
  if (window.__vssEvOff) { try { window.__vssEvOff(); } catch (_) {} window.__vssEvOff = null; }
  try {
    window.__vssEvOff = events.on('devices', () => {
      if (!refreshIfVisible() && window.__vssEvOff) { window.__vssEvOff(); window.__vssEvOff = null; }
    });
  } catch (_) { window.__vssEvOff = null; }

  // Slow safety timer: keeps device approval eventually-visible if the push
  // stream is down. Visible-only, self-cancels when hidden. (Was 3s; the event
  // path makes the common case prompt, so the backstop can be much slower.)
  if (window.__vssPoll) clearInterval(window.__vssPoll);
  window.__vssPoll = setInterval(() => {
    if (!refreshIfVisible()) {
      clearInterval(window.__vssPoll); window.__vssPoll = null;
      if (window.__vssEvOff) { try { window.__vssEvOff(); } catch (_) {} window.__vssEvOff = null; }
    }
  }, 60000);
}

// Renders just the device table into #vss-devices and (re)binds approve/revoke,
// so the poll can refresh it in place without disturbing the QR / add area.
async function renderDevices(userId, msg) {
  const host = document.getElementById('vss-devices');
  if (!host) return;
  let devices = [];
  try { devices = await vss.listDevices(); } catch { /* not enrolled yet */ }
  if (!devices.length) { host.innerHTML = '<p class="vss-muted" style="font-size:14px;">No devices enrolled yet.</p>'; return; }
  host.innerHTML = `<table style="width:100%;border-collapse:collapse;margin-top:8px;font-size:14px;"><tbody>${devices.map((d) => `
    <tr>
      <td style="padding:6px 10px;">${esc(d.label)}</td>
      <td style="padding:6px 10px;"><span style="font-size:11px;padding:2px 8px;border-radius:10px;background:${d.status === 'active' ? '#dcfce7;color:#166534' : '#fef9c3;color:#854d0e'};">${esc(d.status)}</span></td>
      <td style="padding:6px 10px;">${d.status === 'pending' ? `<button data-approve="${esc(d.deviceId)}" data-pub="${esc(d.pubkey)}" class="vss-btn">Approve</button>` : ''}
          <button data-revoke="${esc(d.deviceId)}" class="vss-btn vss-btn-danger">Revoke</button></td>
    </tr>`).join('')}</tbody></table>`;

  host.querySelectorAll('[data-approve]').forEach((b) => {
    b.onclick = async () => {
      try {
        // Out-of-band fingerprint check: the user must confirm the pending
        // device's pubkey matches the code shown on that device. This is what
        // stops a rogue pubkey injected during the pending window from being
        // re-wrapped the user_dek (the approver would see a mismatched code).
        const pub = b.getAttribute('data-pub');
        const fp = await vss.pubkeyFingerprint(pub);
        if (!confirm(`Approve this device only if this code matches the one shown on it (128-bit; match the first two groups for a standard check, all four for high-assurance):\n\n${fp}\n\nIf the codes differ, cancel and revoke the device.`)) {
          msg && msg('Approval cancelled.');
          return;
        }
        msg && msg('Approving...');
        if (!vss.sessionUserDek()) await vss.unlock(userId, localStorage.getItem('vss_device_id'));
        await vss.approveDevice(b.getAttribute('data-approve'), pub);
        msg && msg('Device approved.');
        renderDevices(userId, msg);
      } catch (e) { msg && msg('Approve failed (unlock this device first?): ' + (e.message || e)); }
    };
  });
  host.querySelectorAll('[data-revoke]').forEach((b) => {
    b.onclick = async () => {
      if (!confirm('Revoke this device and rotate your data key?\n\nThis device loses access immediately, and a NEW recovery code is issued (the old one stops working). You must save the new code.')) return;
      try {
        msg && msg('Revoking and rotating data key...');
        const r = await vss.revokeDevice(b.getAttribute('data-revoke'), userId);
        renderDevices(userId, msg);
        if (r && r.backupCode) {
          msg && msg('Device revoked, data key rotated. SAVE your new recovery code: ' + r.backupCode);
          alert('Your data key was rotated. NEW recovery code (save it now, the old one no longer works):\n\n' + r.backupCode);
        } else {
          msg && msg('Device revoked.');
        }
      } catch (e) { msg && msg('Revoke failed (unlock this device first?): ' + (e.message || e)); }
    };
  });
}

let wired = false;
function wire() {
  if (wired) return;
  wired = true;
  const nav = document.querySelector('.nav-item[data-view="vssauth"]');
  if (nav) nav.addEventListener('click', () => setTimeout(render, 30));
  if (document.getElementById('view-vssauth')?.classList.contains('active')) render();
}
window.gdbsOpenVssAuth = () => { window.gdbsShowView?.('vssauth'); setTimeout(render, 30); };

if (document.readyState !== 'loading') wire();
else document.addEventListener('DOMContentLoaded', wire);
